import {
  DATA_UPDATE_SET_COUNTRY,
  DATA_UPDATE_SET_DOC_TYPE,
  DATA_UPDATE_REFERENCE_DATA_REQUEST,
  DATA_UPDATE_REFERENCE_DATA_SUCCESS,
  DATA_UPDATE_REFERENCE_DATA_FAILURE,
  DATA_UPDATE_UPLOAD_START,
  DATA_UPDATE_UPLOAD_PROGRESS,
  DATA_UPDATE_REVIEW_READY,
  DATA_UPDATE_UPLOAD_ERROR,
  DATA_UPDATE_PUBLISH_START,
  DATA_UPDATE_PUBLISH_PROGRESS,
  DATA_UPDATE_PUBLISH_COMPLETE,
  DATA_UPDATE_PUBLISH_ERROR,
  DATA_UPDATE_SEARCH_REQUEST,
  DATA_UPDATE_SEARCH_SUCCESS,
  DATA_UPDATE_SEARCH_FAILURE,
  DATA_UPDATE_SELECT_DOC_REQUEST,
  DATA_UPDATE_SELECT_DOC_SUCCESS,
  DATA_UPDATE_SELECT_DOC_FAILURE,
  DATA_UPDATE_EDIT_SAVE_REQUEST,
  DATA_UPDATE_EDIT_SAVE_SUCCESS,
  DATA_UPDATE_EDIT_SAVE_FAILURE,
  DATA_UPDATE_ABROGATE_REQUEST,
  DATA_UPDATE_ABROGATE_SUCCESS,
  DATA_UPDATE_ABROGATE_FAILURE,
  DATA_UPDATE_RESET_ADD_FLOW,
  DATA_UPDATE_RESET_EDIT_FLOW,
  DATA_UPDATE_RESET_ABROGATE_FLOW,
} from './types'
import { backendClient } from '../../api/backend'
import { backendUrl } from '../../config'

// ─── Simple setters ──────────────────────────────────────────────────────────

export const setCountry = (countryCode) => ({
  type: DATA_UPDATE_SET_COUNTRY,
  payload: countryCode,
})

export const setDocType = (docType) => ({
  type: DATA_UPDATE_SET_DOC_TYPE,
  payload: docType,
})

export const resetAddFlow = () => ({ type: DATA_UPDATE_RESET_ADD_FLOW })
export const resetEditFlow = () => ({ type: DATA_UPDATE_RESET_EDIT_FLOW })
export const resetAbrogateFlow = () => ({ type: DATA_UPDATE_RESET_ABROGATE_FLOW })

// ─── Reference data ──────────────────────────────────────────────────────────

export const fetchReferenceData = (countryCode) => async (dispatch) => {
  dispatch({ type: DATA_UPDATE_REFERENCE_DATA_REQUEST })
  try {
    const data = await backendClient.getDataUpdateReferenceData(countryCode)
    dispatch({ type: DATA_UPDATE_REFERENCE_DATA_SUCCESS, payload: data })
  } catch (error) {
    dispatch({ type: DATA_UPDATE_REFERENCE_DATA_FAILURE, payload: error.message })
  }
}

// ─── Add flow — upload + SSE progress ────────────────────────────────────────

export const uploadDocument = (formData) => async (dispatch) => {
  try {
    const res = await backendClient.uploadDataUpdateDocument(formData)
    const data = await res.json()
    const taskId = data.task_id

    dispatch({ type: DATA_UPDATE_UPLOAD_START, payload: taskId })

    // Connect to SSE stream for progress
    const token = backendClient.getToken()
    const streamUrl = `${backendUrl}api/data-update/tasks/${taskId}/stream`

    const response = await fetch(streamUrl, {
      headers: token && token.access_token
        ? { Authorization: `Bearer ${token.access_token}` }
        : {},
    })

    const reader = response.body.getReader()
    const decoder = new TextDecoder("utf-8")
    let buffer = ''

    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() // keep incomplete line in buffer

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const event = JSON.parse(line.slice(6))
            // Backend sends: {step, progress, message, result, error}
            if (event.error) {
              dispatch({ type: DATA_UPDATE_UPLOAD_ERROR, payload: event.message || 'Processing failed' })
            } else if (event.step === 'review') {
              dispatch({ type: DATA_UPDATE_REVIEW_READY, payload: event.result })
            } else {
              dispatch({
                type: DATA_UPDATE_UPLOAD_PROGRESS,
                payload: { step: event.step, percent: event.progress, message: event.message },
              })
            }
          } catch (e) {
            // ignore parse errors on SSE lines
          }
        }
      }
    }
  } catch (error) {
    dispatch({ type: DATA_UPDATE_UPLOAD_ERROR, payload: error.message })
  }
}

// ─── Add flow — publish (review → publish) ───────────────────────────────────

export const publishDocument = (taskId, metadata, htmlContent) => async (dispatch) => {
  dispatch({ type: DATA_UPDATE_PUBLISH_START, payload: taskId })
  try {
    const data = await backendClient.submitDataUpdateReview(taskId, metadata, htmlContent)

    if (data.task_id) {
      // SSE stream for publish progress
      const token = backendClient.getToken()
      const streamUrl = `${backendUrl}api/data-update/tasks/${data.task_id}/stream`
      const response = await fetch(streamUrl, {
        headers: token && token.access_token
          ? { Authorization: `Bearer ${token.access_token}` }
          : {},
      })

      const reader = response.body.getReader()
      const decoder = new TextDecoder("utf-8")
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop()

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const event = JSON.parse(line.slice(6))
              // Backend sends: {step, progress, message, result, error}
              if (event.error) {
                dispatch({ type: DATA_UPDATE_PUBLISH_ERROR, payload: event.message || 'Publishing failed' })
              } else if (event.step === 'complete') {
                dispatch({ type: DATA_UPDATE_PUBLISH_COMPLETE, payload: event.result || {} })
              } else {
                dispatch({
                  type: DATA_UPDATE_PUBLISH_PROGRESS,
                  payload: { step: event.step, percent: event.progress, message: event.message },
                })
              }
            } catch (e) {
              // ignore
            }
          }
        }
      }
    } else {
      // Synchronous completion
      dispatch({ type: DATA_UPDATE_PUBLISH_COMPLETE, payload: data })
    }
  } catch (error) {
    dispatch({ type: DATA_UPDATE_PUBLISH_ERROR, payload: error.message })
  }
}

// ─── Edit / Abrogate — search documents ──────────────────────────────────────

export const searchDocuments = (params) => async (dispatch) => {
  dispatch({ type: DATA_UPDATE_SEARCH_REQUEST })
  try {
    const data = await backendClient.searchDataUpdateDocuments(params)
    dispatch({
      type: DATA_UPDATE_SEARCH_SUCCESS,
      payload: {
        results: data.results || [],
        total: data.total || 0,
        query: params.query || '',
        page: params.page || 1,
      },
    })
  } catch (error) {
    dispatch({ type: DATA_UPDATE_SEARCH_FAILURE, payload: error.message })
  }
}

// ─── Edit — select document ──────────────────────────────────────────────────

export const selectDocument = (documentId, docType) => async (dispatch) => {
  dispatch({ type: DATA_UPDATE_SELECT_DOC_REQUEST })
  try {
    const data = await backendClient.getDataUpdateDocument(documentId, docType)
    dispatch({ type: DATA_UPDATE_SELECT_DOC_SUCCESS, payload: data })
  } catch (error) {
    dispatch({ type: DATA_UPDATE_SELECT_DOC_FAILURE, payload: error.message })
  }
}

// ─── Edit — update document ──────────────────────────────────────────────────

export const updateDocument = (documentId, payload) => async (dispatch) => {
  dispatch({ type: DATA_UPDATE_EDIT_SAVE_REQUEST })
  try {
    const data = await backendClient.updateDataUpdateDocument(documentId, payload)
    dispatch({ type: DATA_UPDATE_EDIT_SAVE_SUCCESS, payload: data })
    return data
  } catch (error) {
    dispatch({ type: DATA_UPDATE_EDIT_SAVE_FAILURE, payload: error.message })
    throw error
  }
}

// ─── Abrogate ────────────────────────────────────────────────────────────────

export const abrogateDocument = (documentId, payload) => async (dispatch) => {
  dispatch({ type: DATA_UPDATE_ABROGATE_REQUEST })
  try {
    await backendClient.abrogateDataUpdateDocument(documentId, payload)
    dispatch({ type: DATA_UPDATE_ABROGATE_SUCCESS })
  } catch (error) {
    dispatch({ type: DATA_UPDATE_ABROGATE_FAILURE, payload: error.message })
  }
}
